import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"

// Types
export interface Category {
  id: string; name: string; slug: string; description?: string; color?: string; icon?: string
  parent_id?: string; sort_order: number; is_active: boolean; visibility: string
  article_count: number; created_at: string; updated_at: string
}
export interface Article {
  id: string; title: string; slug: string; excerpt?: string; content: string; cover_image?: string
  author_id: string; author_name?: string; category_id?: string; category_name?: string
  series_id?: string; status: string; visibility: string; featured: boolean; pinned: boolean
  read_time_minutes: number; meta_title?: string; meta_description?: string
  approval_status?: string; approved_by_id?: string; approved_by_name?: string
  approved_at?: string; approval_comment?: string
  view_count: number; like_count: number; comment_count: number; share_count: number
  published_at?: string; created_at: string; updated_at: string
}
export interface ArticleList { items: Article[]; total: number; page: number; per_page: number; pages: number }
export interface CategoryList { items: Category[]; total: number }

export interface ContentSource {
  id: string; name: string; source_type: string; config?: Record<string, unknown>
  schedule_cron?: string; is_active: boolean; last_fetch?: string
  fetch_count: number; error_count: number; last_error?: string
  created_by_id: string; created_at: string; updated_at: string
}
export interface SocialPost {
  id: string; content: string; media_urls?: string[]; link_url?: string
  article_id?: string; account_id: string; platform: string
  scheduled_at?: string; published_at?: string; status: string; error_message?: string
  external_url?: string; created_by_id: string
  impressions: number; engagements: number; clicks: number; shares: number; comments: number
  created_at: string; updated_at: string
}
export interface SocialPostList { items: SocialPost[]; total: number; page: number; per_page: number; pages: number }

// Categories
export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => { const { data } = await api.get<CategoryList>("/v1/content/categories"); return data },
  })
}
export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: Record<string, unknown>) => { const { data } = await api.post<Category>("/v1/content/categories", d); return data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  })
}
export function useUpdateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: d }: { id: string; data: Record<string, unknown> }) => {
      const { data } = await api.put<Category>(`/v1/content/categories/${id}`, d)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  })
}
export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/v1/content/categories/${id}`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  })
}

// Articles
export function useArticles(params: { page?: number; perPage?: number; search?: string; status?: string; categoryId?: string; approvalStatus?: string } = {}) {
  return useQuery({
    queryKey: ["articles", params],
    queryFn: async () => {
      const q: Record<string, string | number> = { page: params.page || 1, per_page: params.perPage || 20 }
      if (params.search) q.search = params.search
      if (params.status) q.status = params.status
      if (params.categoryId) q.category_id = params.categoryId
      if (params.approvalStatus) q.approval_status = params.approvalStatus
      const { data } = await api.get<ArticleList>("/v1/content/articles", { params: q })
      return data
    },
  })
}
export function useCreateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: Record<string, unknown>) => { const { data } = await api.post<Article>("/v1/content/articles", d); return data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  })
}
export function useUpdateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: d }: { id: string; data: Record<string, unknown> }) => { const { data } = await api.put<Article>(`/v1/content/articles/${id}`, d); return data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  })
}
export function useDeleteArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/v1/content/articles/${id}`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  })
}
export function usePublishArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { const { data } = await api.post<Article>(`/v1/content/articles/${id}/publish`); return data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  })
}
export function useApproveArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status, comment }: { id: string; status: string; comment?: string }) => {
      const { data } = await api.post<Article>(`/v1/content/articles/${id}/approve`, { status, comment })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  })
}

// Sources
export function useContentSources() {
  return useQuery({
    queryKey: ["content-sources"],
    queryFn: async () => { const { data } = await api.get<{ items: ContentSource[]; total: number }>("/v1/content/sources"); return data },
  })
}

// Social Posts
export function useSocialPosts(params: { page?: number; perPage?: number; status?: string } = {}) {
  return useQuery({
    queryKey: ["social-posts", params],
    queryFn: async () => {
      const q: Record<string, string | number> = { page: params.page || 1, per_page: params.perPage || 20 }
      if (params.status) q.status = params.status
      const { data } = await api.get<SocialPostList>("/v1/content/social-posts", { params: q })
      return data
    },
  })
}

export function useBulkCreateSocialPosts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { content: string; media_urls?: string[]; link_url?: string; article_id?: string; account_ids: string[]; scheduled_at?: string }) => {
      const { data } = await api.post<SocialPost[]>("/v1/content/social-posts/bulk", payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-posts"] }),
  })
}

// Social Accounts
export interface SocialAccount {
  id: string; platform: string; account_name: string; account_id: string
  account_url: string | null; owner_id: string; owner_name?: string | null; is_active: boolean
  last_used_at: string | null; created_at: string; updated_at: string
}
export interface SocialAccountList { items: SocialAccount[]; total: number }

export function useSocialAccounts() {
  return useQuery({
    queryKey: ["social-accounts"],
    queryFn: async () => { const { data } = await api.get<SocialAccountList>("/v1/content/social-accounts"); return data },
  })
}
export function useConnectSocialAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { platform: string; account_name: string; account_id: string; account_url?: string; access_token?: string; refresh_token?: string }) => {
      const { data } = await api.post("/v1/content/social-accounts", payload); return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-accounts"] }),
  })
}
export function useDisconnectSocialAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/v1/content/social-accounts/${id}`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-accounts"] }),
  })
}
